import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Facebook, Instagram } from "lucide-react";

const navItems = [
  { to: "/", label: "Home" },
  { to: "/gallery", label: "Gallery" },
  { to: "/shop", label: "Shop" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/feedback", label: "Feedback" },
  { to: "/contact", label: "Contact" },
];

const socials = [
  {
    href: "https://www.facebook.com/profile.php?id=61584658451390",
    label: "Facebook",
    Icon: Facebook,
    testid: "social-facebook",
    bg: "#1877F2", // Facebook blue
    fill: "#1877F2",
  },
  {
    href: "https://www.instagram.com/shopxemi/",
    label: "Instagram",
    Icon: Instagram,
    testid: "social-instagram",
    // Instagram official gradient (yellow → orange → magenta → purple)
    bg: "linear-gradient(45deg, #FEDA75 0%, #FA7E1E 25%, #D62976 50%, #962FBF 75%, #4F5BD5 100%)",
    fill: "url(#ig-grad)",
  },
];

export default function Layout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAFAFA] text-[#09090B]">
      <header
        data-testid="site-header"
        className="sticky top-0 z-50 backdrop-blur-xl bg-white/75 border-b border-black/10"
      >
        <div className="px-6 md:px-12 lg:px-20 flex items-center justify-between h-24 md:h-28">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
            <img
              src="/logo.png"
              alt="Xemi logo"
              width="120"
              height="120"
              loading="eager"
              decoding="sync"
              style={{ imageRendering: "auto" }}
              className="w-16 h-16 md:w-24 md:h-24 object-contain shrink-0"
            />
            <span className="font-display font-black text-3xl md:text-4xl tracking-tighter">XEMI</span>
            <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#71717A] hidden sm:inline">
              /print
            </span>
            <span className="ml-1 inline-block w-2 h-2 bg-[#E6007A]" />
          </Link>

          <nav className="hidden md:flex items-center gap-7 lg:gap-9">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `font-mono text-sm font-bold uppercase tracking-[0.18em] transition-all hover:text-black relative pb-1 ${
                    isActive ? "text-black" : "text-[#27272A]"
                  } ${isActive ? "after:absolute after:left-0 after:bottom-0 after:h-[3px] after:w-full after:bg-[#00E5FF]" : "hover:after:absolute hover:after:left-0 hover:after:bottom-0 hover:after:h-[3px] hover:after:w-full hover:after:bg-[#FFD600]"}`
                }
              >
                {item.label}
              </NavLink>
            ))}
            <div className="flex items-center gap-2">
              {socials.map(({ href, label, Icon, testid, bg }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`header-${testid}`}
                  aria-label={`Xemi on ${label}`}
                  style={{ background: bg }}
                  className="w-10 h-10 inline-flex items-center justify-center text-white shadow-md hover:scale-110 hover:shadow-lg transition-all"
                >
                  <Icon size={20} fill="currentColor" strokeWidth={0} />
                </a>
              ))}
            </div>
            <Link
              to="/contact"
              data-testid="header-cta"
              className="btn-ink bg-black text-white px-5 py-3 text-xs font-mono uppercase tracking-[0.2em]"
            >
              Get Quote
            </Link>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="md:hidden p-2 -mr-2"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-black/10 bg-white">
            <div className="px-6 py-6 flex flex-col gap-4">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `font-display text-2xl font-black ${
                      isActive ? "text-black" : "text-[#52525B]"
                    }`
                  }
                >
                  {item.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-3 mt-4 pt-4 border-t border-black/10">
                {socials.map(({ href, label, Icon, testid, bg }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`mobile-${testid}`}
                    aria-label={`Xemi on ${label}`}
                    style={{ background: bg }}
                    className="w-12 h-12 inline-flex items-center justify-center text-white shadow-md transition-all"
                  >
                    <Icon size={22} fill="currentColor" strokeWidth={0} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer
        data-testid="site-footer"
        className="border-t border-black/10 bg-white"
      >
        <div className="px-6 md:px-12 lg:px-20 py-16 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-6">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#71717A]">
              Xemi — Direct-to-Film Print Co.
            </p>
            <h3 className="font-display font-black text-5xl md:text-6xl mt-4 leading-[0.95]">
              Ready to print
              <br />
              <span className="text-[#E6007A]">your idea</span>?
            </h3>
            <Link
              to="/contact"
              data-testid="footer-cta"
              className="btn-ink-magenta inline-block mt-8 bg-black text-white px-7 py-4 text-xs font-mono uppercase tracking-[0.2em]"
            >
              Start a project →
            </Link>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#71717A] mb-4">
              Studio
            </p>
            <ul className="space-y-2 font-mono text-sm">
              <li>Shopxemi@gmail.com</li>
              <li>07828 274315</li>
              <li>Mon–Fri · 09—18</li>
            </ul>
            <div className="flex items-center gap-3 mt-5">
              {socials.map(({ href, label, Icon, testid, bg }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={testid}
                  aria-label={`Xemi on ${label}`}
                  style={{ background: bg }}
                  className="w-11 h-11 inline-flex items-center justify-center text-white shadow-md hover:scale-110 hover:shadow-lg transition-all"
                >
                  <Icon size={20} fill="currentColor" strokeWidth={0} />
                </a>
              ))}
            </div>
          </div>
          <div className="md:col-span-3">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#71717A] mb-4">
              Sitemap
            </p>
            <ul className="space-y-2 font-mono text-sm">
              {navItems.map((i) => (
                <li key={i.to}>
                  <Link to={i.to} className="hover:text-[#E6007A] transition-colors">
                    {i.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/shop#refund-policy" className="hover:text-[#FFD600] transition-colors">
                  Refunds
                </Link>
              </li>
              <li>
                <Link to="/admin/login" className="hover:text-[#00E5FF] transition-colors">
                  Admin
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-black/10 px-6 md:px-12 lg:px-20 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-mono text-xs text-[#71717A]">
            © {new Date().getFullYear()} Xemi — direct-to-film, made with ink.
          </p>
          <div className="flex gap-2">
            <span className="w-3 h-3 bg-[#00E5FF]" />
            <span className="w-3 h-3 bg-[#E6007A]" />
            <span className="w-3 h-3 bg-[#FFD600]" />
            <span className="w-3 h-3 bg-black" />
          </div>
        </div>
      </footer>
    </div>
  );
}
